import { getDates } from '@zenos/front-end-util-utils';
import { useLayoutEffect, useRef, useState } from 'react';
import { useAppState } from './useAppState.ts';
import { parseWeekFromSearchParams } from '../utils/parseWeekFromSearchParams.ts';

type UsePersistedWeekStartParams = {
  storageKey: string;
  searchParams: URLSearchParams;
};

export function usePersistedWeekStart({
  storageKey,
  searchParams,
}: UsePersistedWeekStartParams) {
  const {
    state: { date },
    dispatch,
  } = useAppState();

  const [weekReady, setWeekReady] = useState(false);
  const weekInitialized = useRef(false);

  useLayoutEffect(() => {
    if (weekInitialized.current) return;

    const fromUrl = parseWeekFromSearchParams(searchParams);

    if (fromUrl) {
      const { startDate: startOfWeek } = getDates(fromUrl);

      dispatch({
        type: 'SET_DATE',
        payload: { selectedDate: fromUrl, startOfWeek },
      });
    } else if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      const parsedSaved = saved ? new Date(saved) : null;

      if (parsedSaved && !Number.isNaN(parsedSaved.getTime())) {
        const { startDate: startOfWeek } = getDates(parsedSaved);

        dispatch({
          type: 'SET_DATE',
          payload: { selectedDate: parsedSaved, startOfWeek },
        });
      } else {
        const today = new Date();
        const { startDate: startOfWeek } = getDates(today);

        dispatch({
          type: 'SET_DATE',
          payload: { selectedDate: today, startOfWeek },
        });
      }
    }

    weekInitialized.current = true;
    setWeekReady(true);
  }, [dispatch, searchParams, storageKey]);

  useLayoutEffect(() => {
    if (!weekReady) return;

    localStorage.setItem(storageKey, date.startOfWeek.toISOString());
  }, [date.startOfWeek, storageKey, weekReady]);

  return { weekReady, startOfWeek: date.startOfWeek };
}
